/* eslint-disable react/jsx-max-depth */
import React from 'react';
import ReactDOM from 'react-dom';
import './index.scss';
import { Provider } from 'react-redux';
import { Switch } from 'react-router-dom';
import { PersistGate } from 'redux-persist/lib/integration/react';
import ReactBreakpoints from 'react-breakpoints';
import IntlProviderContainer from 'settings/IntlProvider';
import * as Sentry from '@sentry/react';
import { SENTRY_DSN } from 'settings/constants';
import { ConnectedRouter } from 'connected-react-router';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import App from './App';
import { persistor, store, history } from './store/Store';
import ApplicationRoutes from './Routes/Routes';
import AppRoute from './Routes/AppRoute';
import { ThemeProvider } from './Context';
import { ThemeProvider as StyledThemeProvider, StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import useTheme from 'Hooks/UseTheme';

// styled-components v6 no longer ships a default `shouldForwardProp` filter — every custom
// prop used purely for styling (e.g. `isActive`, `disabled` on a `styled.div`) would now leak
// onto the underlying DOM element and trigger "React does not recognize the X prop" warnings,
// unless every one of the ~1140 files using `styled.tag` is migrated to the new `$transient`
// prop convention. Restoring v5's exact filtering behavior here (only forward valid HTML/SVG
// attributes to native elements; forward everything to custom React components) keeps every
// existing usage working unchanged without that repo-wide rewrite.
const shouldForwardProp = (propName, target) => (
  typeof target === 'string' ? isPropValid(propName) : true
);

const breakpoints = {
  first: 900,
  second: 1200,
  third: 1800,
  fourth: 2500
};

const { Routes } = ApplicationRoutes;

Sentry.init({
  dsn: SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration(
    {
      beforeStartSpan: (options) => ({
        ...options,
        name: window.location.pathname
          .replace(/[a-f0-9]{32}/g, '<hash>')
          .replace(/\d+/g, '<digits>')
      })
    }
  )],
  tracesSampleRate: 1.0 // Be sure to lower this in production
});

Sentry.setTag('build_version', process.env.REACT_APP_VERSION);

// Global store reference - used for fetchApi dispatch
window.store = store;

const ThemedApp = () => {
  const { themeStyles } = useTheme();

  return (
    <StyledThemeProvider theme={ themeStyles }>
      <App>
        <Switch>
          {Routes.map((route) => (
            <AppRoute
              key={ route.path || 'default' }
              exact={ route.exact }
              path={ route.path }
              component={ route.component }
              isPrivate={ route.isPrivate }
              requireWorkspace={ route.requireWorkspace }
              edit={ route.edit }
              isNew={ route.isNew }
            />
          ))}
        </Switch>
      </App>
    </StyledThemeProvider>
  );
};

ReactDOM.render(
  <StyleSheetManager shouldForwardProp={ shouldForwardProp } enableVendorPrefixes>
    <Provider store={ store }>
      <IntlProviderContainer>
        <PersistGate persistor={ persistor }>
          <ConnectedRouter history={ history }>
            <ReactBreakpoints breakpoints={ breakpoints }>
              <DndProvider backend={ HTML5Backend }>
                <ThemeProvider>
                  <ThemedApp />
                </ThemeProvider>
              </DndProvider>
            </ReactBreakpoints>
          </ConnectedRouter>
        </PersistGate>
      </IntlProviderContainer>
    </Provider>
  </StyleSheetManager>, document.getElementById('root')
);
